<template>
    <div class="detailed-competences-modal">
        <el-dialog
            :title="
                $trans('web_profile.select_up_to_twelve_additional_competences')
            "
            :model-value="WebProfileStore.isEditDetailedCompetencesModalOpen"
            width="800px"
            :show-close="false"
            class="custom-modal"
            @close="close"
        >
            <span class="dialog-cancel-text" @click="close">
                {{ $trans("profile_dossier.cancel") }}
                <el-icon><Close /></el-icon>
            </span>

            <el-button type="primary" @click="save">
                {{ $trans("web_profile.save") }}
            </el-button>

            <div class="table-container" v-loading="isCompetencesLoading">
                <el-input
                    v-model="searchCompetences"
                    :placeholder="$trans('profile_dossier.search_placeholder')"
                    clearable
                    class="custom-search-input"
                >
                    <template #suffix>
                        <img
                            src="/media/images/search_gray.png"
                            alt="Search"
                            style="width: 16px; height: 16px; opacity: 0.6"
                        />
                    </template>
                </el-input>

                <EasyDataTable
                    v-if="!isCompetencesLoading"
                    :headers="competenceHeaders"
                    :items="sortedCompetences"
                    :search-value="searchCompetences"
                    table-class-name="competences-table"
                    :rows-per-page="10"
                    buttons-pagination
                >
                    <template #item-action="{ id, is_selected }">
                        <el-checkbox
                            :model-value="is_selected"
                            @change="handleToggle(id)"
                            :disabled="!is_selected && isMaxSelected"
                        />
                    </template>

                    <template #header-duration>
                        <div class="custom-table-header">
                            <span
                                @click.stop="changeSort('duration_in_month')"
                                :class="{
                                    active: sortBy === 'duration_in_month',
                                }"
                            >
                                {{ $trans("profile_dossier.duration") }}
                                <SortIcons
                                    :columnKey="'duration_in_month'"
                                    :sortBy="sortBy"
                                    :sortDesc="sortDesc"
                                />
                            </span>
                        </div>
                    </template>

                    <template #header-name>
                        <div class="custom-table-header">
                            <span
                                @click.stop="changeSort('name')"
                                :class="{ active: sortBy === 'name' }"
                            >
                                {{ $trans("profile_dossier.competences") }}
                                <SortIcons
                                    :columnKey="'name'"
                                    :sortBy="sortBy"
                                    :sortDesc="sortDesc"
                                />
                            </span>
                        </div>
                    </template>
                </EasyDataTable>
            </div>
        </el-dialog>
    </div>
</template>

<script>
import { mapStores } from "pinia";
import { useWebProfileStore } from "@/stores/WebProfileStore";
import EasyDataTable from "vue3-easy-data-table";
import SortIcons from "./../../../ProfileDossier/Components/Tables/SortIcons.vue";
import axios from "axios";
import { Close } from "@element-plus/icons-vue";

export default {
    name: "EditDetailedCompetencesModal",
    components: { EasyDataTable, SortIcons, Close },
    data() {
        return {
            localCompetenceItems: [],
            searchCompetences: "",
            sortBy: "",
            sortDesc: false,
            isCompetencesLoading: false,
        };
    },
    computed: {
        ...mapStores(useWebProfileStore),

        isModalOpen() {
            return this.WebProfileStore.isEditDetailedCompetencesModalOpen;
        },

        selectedCount() {
            return this.localCompetenceItems.filter((i) => i.is_selected)
                .length;
        },

        isMaxSelected() {
            return this.selectedCount >= 12;
        },

        competenceHeaders() {
            return [
                {
                    text: this.$trans("profile_dossier.action"),
                    value: "action",
                    width: 50,
                    sortable: false,
                },
                {
                    text: this.$trans("profile_dossier.duration"),
                    value: "duration",
                    sortable: false,
                },
                {
                    text: this.$trans("profile_dossier.competences"),
                    value: "name",
                    sortable: false,
                },
            ];
        },

        sortedCompetences() {
            if (!this.sortBy) return this.localCompetenceItems;
            return [...this.localCompetenceItems].sort((a, b) => {
                let aVal = a[this.sortBy];
                let bVal = b[this.sortBy];
                if (typeof aVal === "string") aVal = aVal.toLowerCase();
                if (typeof bVal === "string") bVal = bVal.toLowerCase();
                if (aVal < bVal) return this.sortDesc ? 1 : -1;
                if (aVal > bVal) return this.sortDesc ? -1 : 1;
                return 0;
            });
        },
    },

    watch: {
        isModalOpen(newVal) {
            if (newVal) {
                this.localCompetenceItems = [];
                this.fetchModalCompetences();
                this.sortBy = "";
                this.sortDesc = false;
            }
        },
    },

    methods: {
        close() {
            this.WebProfileStore.closeEditDetailedCompetencesModal();
        },

        async save() {
            const selected = this.localCompetenceItems.filter(
                (i) => i.is_selected
            );

            try {
                await this.WebProfileStore.updateDetailedCompetences(selected);
                this.$message.success(
                    this.$trans("web_profile.detailed_competences_saved")
                );
            } catch (e) {
                console.error(e);
                this.$message.error(this.$trans("global.something_went_wrong"));
            }
        },
        async fetchModalCompetences() {
            if (this.isCompetencesLoading) return;
            this.isCompetencesLoading = true;

            try {
                const profileId = this.WebProfileStore.profile.id;
                const current = this.WebProfileStore.detailedCompetences;
                const selectedIds = new Set(current.map((c) => c.id));

                const response = await axios.get(
                    `/candidates/${profileId}/web-profile/competences/detail`
                );

                const data = response.data;

                this.localCompetenceItems = data
                    .map((item) => ({
                        ...item,
                        is_selected: selectedIds.has(item.id),
                        duration_in_month:
                            parseInt(item.duration_in_month, 10) || 0,
                    }))
                    .sort((a, b) => {
                        if (a.is_selected === b.is_selected)
                            return b.duration_in_month - a.duration_in_month;
                        return a.is_selected ? -1 : 1;
                    });
            } catch (e) {
                console.error(e);
            } finally {
                this.isCompetencesLoading = false;
            }
        },

        changeSort(key) {
            if (this.sortBy === key) {
                this.sortDesc = !this.sortDesc;
            } else {
                this.sortBy = key;
                this.sortDesc = false;
            }
        },

        handleToggle(id) {
            const item = this.localCompetenceItems.find((i) => i.id === id);
            if (!item) return;
            if (this.isMaxSelected && !item.is_selected) return;
            item.is_selected = !item.is_selected;
        },
    },
};
</script>

<style scoped>
.detailed-competences-modal :deep(.el-dialog) {
    --el-dialog-width: 750px;
    top: -100px !important;
}

.table-container {
    width: 100%;
    box-sizing: border-box;
    padding-bottom: 24px;
    padding-top: 10px;
    background-color: #ededed;
    font-family: "Barlow", sans-serif;
    padding-left: 14px;
    padding-right: 14px;
}

:deep(.el-input) {
    width: 40%;
}

:deep(.el-dialog__header) {
    padding-bottom: 40px;
}

:deep(.el-input__prefix) {
    width: 0 !important;
}

:deep(.el-input__wrapper.is-focus) {
    border: 2px solid #41abc4 !important;
    box-shadow: none !important;
}

.competences-table {
    width: 100%;
    --easy-table-header-font-size: 14px;
    --easy-table-body-row-font-size: 14px;
    --easy-table-body-row-hover-background-color: #fff;
}

:deep(.vue3-easy-data-table) {
    margin-bottom: unset !important;
    border: none !important;
    background-color: #ededed;
}

:deep(.vue3-easy-data-table tr th) {
    border-top: 0;
    border-bottom: 0;
    background: rgb(237, 237, 237);
    padding: 0 0.5rem;
    color: #3c3c3c;
    font-style: normal;
    font-weight: 400;
    font-size: 16px !important;
    height: 24px;
    line-height: 24px;
    font-family: sans-serif;
    border: 2px solid #ededed;
}

:deep(.vue3-easy-data-table tr td) {
    height: 1px;
    background: #ffffff !important;
    padding: 0.5rem !important;
    line-height: 20px !important;
    border: 2px solid #ededed !important;
    font-size: 16px !important;
    font-weight: 700 !important;
}

:deep(.vue3-easy-data-table__footer) {
    background-color: transparent !important;
    justify-content: center !important;
}

:deep(.pagination__rows-per-page) {
    display: none !important;
}

:deep(.pagination__items-index) {
    display: none !important;
}

:deep(.buttons-pagination) {
    transform: scale(1.5);
    margin-top: 25px;
}

:deep(.buttons-pagination .item.button) {
    border-radius: 0;
}

:deep(.buttons-pagination .item.button.active) {
    color: #fff;
    background: linear-gradient(
        90deg,
        rgb(65, 171, 196) 0%,
        rgb(119, 214, 241) 100%
    );
    border-color: rgb(65, 171, 196) !important;
    border-radius: 0;
}

:deep(.next-page__click-button .arrow) {
    margin-top: 21px;
    margin-left: 40px;
}

:deep(.previous-page__click-button .arrow) {
    margin-right: 40px;
    margin-top: 21px;
}

/* SortIcons — strzałki będą widoczne */
.custom-table-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    cursor: pointer;
    user-select: none;
}

.custom-table-header span {
    display: flex;
    align-items: center;
    width: 100%;
    justify-content: space-between;
}

.custom-table-header span.active {
    font-weight: 400;
}

/* ważne: włącz strzałki, nie ukrywaj */
:deep(.sortType-icon) {
    display: inline-flex !important;
}

:deep(.vue3-easy-data-table__body tr td:first-child) {
    background-color: #d2e1e5 !important;
    text-align: center !important;
}

:deep(.vue3-easy-data-table__body tr:hover td:first-child) {
    background-color: #d2e1e5 !important;
}

:deep(.el-input__wrapper) {
    border-radius: 0 !important;
    margin-bottom: 20px;
    height: 37px;
}

.search-icon {
    width: 16px;
    height: 16px;
    opacity: 0.6;
}

.el-button {
    float: right;
    margin-top: -35px;
    background-color: #41abc4;
    border-color: #41abc4;
    border-radius: unset;
    width: 200px;
    font-size: 16px !important;
    font-weight: 700 !important;
}

:deep(.el-dialog__title) {
    position: relative;
    font-size: 1.5rem;
}

.dialog-cancel-text {
    position: absolute;
    top: 5px;
    right: 21px;
    cursor: pointer;
    font-size: 14px;
    color: #909399;
    font-weight: 500;
    z-index: 2000;
    user-select: none;
}

.dialog-cancel-text:hover {
    color: #41abc4;
}

.dialog-cancel-text :deep(.el-icon) {
    top: 3px;
    transform: scale(1.4);
}
</style>
